/**
 * Shared chart styling constants.
 * Uses CSS variables from the design system where possible.
 */

/**
 * HTML data-attribute names used by the embedded-chart Tiptap node and
 * the AI marker contract. Single source of truth — the sanitizer
 * allowlist, the Tiptap node, the export rasterizer, and the system
 * prompt all reference these so the names can't drift.
 */
export const CHART_NODE_ATTR = "data-klarify-chart";
export const CHART_NODE_CONFIG_ATTR = "data-config";
/** AI marker for "drop the Nth newly-generated chart here". */
export const CHART_PLACEHOLDER_ATTR = "data-chart-placeholder";
/** Marker the editor replaces existing charts with before sending to the AI. */
export const EXISTING_CHART_MARKER_ATTR = "data-existing-chart";

/**
 * Tiptap transaction meta key marking edits originated from a chart NodeView.
 * The document-editor's `onUpdate` handler bypasses its `editable=false`
 * gate when this flag is set, so chart edits made while the document is
 * in saved mode still persist to the database.
 */
export const CHART_EDIT_TRANSACTION_META = "klarifyChartEdit";

/**
 * Pause after a typed change in the live chart editor before it writes the
 * document; each write re-renders every chart in the report.
 */
export const CHART_EDIT_TYPING_DEBOUNCE_MS = 250;

/** Visualization types accepted by the embedded chart node. */
export const VALID_VISUALIZATION_TYPES = ["chart", "diagram"] as const;

/** Data URL prefix for PNG-encoded canvas captures used in document exports. */
export const PNG_DATA_URL_PREFIX = "data:image/png;base64,";

/** Match `<div data-chart-placeholder="N"></div>` markers emitted by the AI. */
export const CHART_PLACEHOLDER_REGEX = new RegExp(
	`<div\\b[^>]*\\b${CHART_PLACEHOLDER_ATTR}=["'](\\d+)["'][^>]*>\\s*</div>`,
	"g",
);

/** Match `<div data-existing-chart="N"></div>` markers we substitute for existing charts before sending to the AI. */
export const EXISTING_CHART_MARKER_REGEX = new RegExp(
	`<div\\b[^>]*\\b${EXISTING_CHART_MARKER_ATTR}=["'](\\d+)["'][^>]*>\\s*</div>`,
	"g",
);

/** Marker the server replaces an inline image with before an edit run sends section text to the AI. */
export const EXISTING_IMAGE_MARKER_ATTR = "data-existing-image";

/** Match `<img data-existing-image="N">` markers, self-closed or not. */
export const EXISTING_IMAGE_MARKER_REGEX = new RegExp(
	`<img\\b[^>]*\\b${EXISTING_IMAGE_MARKER_ATTR}=["'](\\d+)["'][^>]*>`,
	"g",
);

/** Match any `<img>` tag; a quoted attribute value may hold a raw `>`. */
export const IMAGE_TAG_REGEX = /<img\b(?:[^>"']|"[^"]*"|'[^']*')*>/gi;

/**
 * Match the rendered KlarifyChartNode wrapper div so we can replace it with a
 * stable marker before sending section content to the AI. Order of attributes
 * is not guaranteed — `data-config` is pulled out separately.
 */
export const EXISTING_CHART_BLOCK_REGEX = new RegExp(
	`<div\\b[^>]*\\b${CHART_NODE_ATTR}\\b[^>]*>\\s*<\\/div>`,
	"g",
);

/** Extract the `data-config` attribute value from a chart node div (double-quoted). */
export const CHART_DATA_CONFIG_REGEX = new RegExp(
	`\\b${CHART_NODE_CONFIG_ATTR}="([^"]*)"`,
);

/** Default chart-1 token fallback hex — reused as last-resort palette color. */
export const CHART_PRIMARY_FALLBACK_HEX = "#4f46e5";
/** Token-backed default for newly added series in the chart editor. */
export const DEFAULT_NEW_SERIES_COLOR = `var(--color-chart-1, ${CHART_PRIMARY_FALLBACK_HEX})`;
/** Token-backed default for new reference lines in the chart editor. */
export const DEFAULT_REFERENCE_LINE_COLOR = "var(--muted-foreground, #94a3b8)";
/** Token-backed default fill for new shaded range bands in the chart editor. */
export const DEFAULT_REFERENCE_BAND_COLOR = "var(--muted-foreground, #94a3b8)";

/** Default color palette for multi-series cartesian charts (bar, line, area, radar, scatter) */
export const CHART_COLORS = [
	"var(--color-chart-1, #4f46e5)",
	"var(--color-chart-2, #0891b2)",
	"var(--color-chart-3, #059669)",
	"var(--color-chart-4, #d97706)",
	"var(--color-chart-5, #dc2626)",
	"#7c3aed",
	"#db2777",
	"#2563eb",
	"#0d9488",
	"#ca8a04",
];

/** Indigo gradient palette for funnel charts */
export const FUNNEL_COLORS = [
	"#4f46e5",
	"#6366f1",
	"#818cf8",
	"#a5b4fc",
	"#c7d2fe",
	"#ddd6fe",
	"#e9d5ff",
	"#f3e8ff",
];

/** Shared tooltip styling matching shadcn design system */
export const TOOLTIP_STYLE = {
	borderRadius: 8,
	border: "1px solid var(--border, #e5e7eb)",
	boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
	fontSize: 12,
} as const;

/** Default reference line color (muted foreground) */
export const REFERENCE_LINE_COLOR = "var(--muted-foreground, #94a3b8)";

/** Default fill color for shaded range bands (muted foreground) */
export const REFERENCE_BAND_COLOR = "var(--muted-foreground, #94a3b8)";
/** Default fill opacity for shaded range bands — kept low so data stays legible. */
export const REFERENCE_BAND_OPACITY = 0.15;

/** Text color of the section labels under a profile's x axis. */
export const SECTION_LABEL_COLOR = "var(--muted-foreground, #475569)";

/**
 * strokeDasharray values the graph library writes for reference lines: the
 * default dash (the editor's "Dotted line") and solid. The renderers draw
 * an unset value dashed, so a solid line stores "none".
 */
export const REFERENCE_LINE_SOLID_DASH = "none";
export const REFERENCE_LINE_DEFAULT_DASH = "4 3";

/** Line shape of line/area charts that do not set `lineInterpolation`. */
export const DEFAULT_LINE_INTERPOLATION = "monotone";

/** Line shape of new graph-library frames: score profiles join points with straight segments. */
export const GRAPH_FRAME_LINE_INTERPOLATION = "linear";

/** Chart types whose series draw a line between points, so `lineInterpolation` applies. */
export const LINE_SHAPE_CHART_TYPES: ReadonlySet<string> = new Set([
	"line",
	"area",
]);

/** Get a color from the palette by index, with fallback */
export function getChartColor(index: number, palette = CHART_COLORS): string {
	return palette[index % palette.length] ?? palette[0] ?? "#4f46e5";
}

/**
 * Preset colors for reference lines and norm zones in the define-graph
 * editor. Concrete hexes (not CSS variables) because chart configs render
 * server-side in exports where design tokens do not resolve. Values mirror
 * the klarify-* 400/500 tokens in globals.css.
 */
export const CHART_REFERENCE_COLOR_PRESETS = [
	{ key: "green", hex: "#78c091" },
	{ key: "peach", hex: "#f07e52" },
	{ key: "rose", hex: "#ff1f57" },
	{ key: "ocean", hex: "#266f78" },
	{ key: "gray", hex: "#94a3b8" },
] as const;

const CHART_REFERENCE_PRESET_HEX = Object.fromEntries(
	CHART_REFERENCE_COLOR_PRESETS.map((preset) => [preset.key, preset.hex]),
) as Record<(typeof CHART_REFERENCE_COLOR_PRESETS)[number]["key"], string>;

/**
 * Norm-zone fills the catalog frames share. The average and clinical zones
 * take the editor presets; the at-risk zone between them is an amber the
 * preset list does not carry (a new preset also needs a swatch label in the
 * define-graph editor).
 */
export const BUILT_IN_GRAPH_PRESET_ZONE_COLORS = {
	average: CHART_REFERENCE_PRESET_HEX.green,
	atRisk: "#f5c563",
	clinical: CHART_REFERENCE_PRESET_HEX.peach,
} as const;

/** All GraphPreset.scale values. Mirrors the Prisma `GraphScale` enum. */
export const GRAPH_SCALES = [
	"T_SCORE",
	"STANDARD_SCORE",
	"SCALED_SCORE",
	"PERCENTILE",
	"RAW",
] as const;
export type GraphScaleValue = (typeof GRAPH_SCALES)[number];

/** All GraphPreset.status values. Mirrors the Prisma `GraphPresetStatus` enum. */
export const GRAPH_PRESET_STATUSES = [
	"EXTRACTING",
	"DRAFT",
	"READY",
	"FAILED",
	"SKIPPED",
] as const;
export type GraphPresetStatusValue = (typeof GRAPH_PRESET_STATUSES)[number];

/**
 * Statuses a template outline may reference. The Studio canvas shows DRAFT
 * graphs the section chat created, so the save gates must accept them too.
 */
export const STUDIO_GRAPH_PRESET_STATUSES = ["READY", "DRAFT"] as const;

/** Where a preset's frame came from; stored in `details.sourceProvenance`. */
export const GRAPH_PRESET_PROVENANCES = [
	"excel",
	"screenshot",
	"templateImport",
	"savedFromReport",
	"builtIn",
	"manual",
] as const;
export type GraphPresetProvenance = (typeof GRAPH_PRESET_PROVENANCES)[number];

/** How a graph frame gets its x-categories at generation time. */
export const GRAPH_CATEGORY_MODES = ["fromDocument", "fixed"] as const;
export type GraphCategoryMode = (typeof GRAPH_CATEGORY_MODES)[number];

/** Max graph blocks attached across one template's section briefs. */
export const MAX_GRAPH_ATTACHMENTS_PER_TEMPLATE = 25;

/**
 * Row shape of normalCurve data extractions: one row per score track.
 * Shared by the extraction prompt, the fulfillment filter, and
 * mergePresetData so the contract cannot drift.
 */
export const NORMAL_CURVE_ROW_KEYS = {
	track: "track",
	value: "value",
	ciLow: "ciLow",
	ciHigh: "ciHigh",
} as const;

/**
 * Score types a chart can plot (chartConfigSchema.scoreScale). camelCase
 * matches normalCurveTrackTypeSchema; "raw" is a value with no known frame.
 */
export const CHART_SCORE_SCALES = [
	"percentile",
	"standardScore",
	"tScore",
	"scaledScore",
	"zScore",
	"stanine",
	"raw",
] as const;
export type ChartScoreScale = (typeof CHART_SCORE_SCALES)[number];

interface ChartScoreScaleDefaults {
	readonly yDomain: readonly [number, number] | null;
	readonly yTickInterval: number | null;
	readonly mean: number | null;
	readonly sd: number | null;
}

/** Object.freeze is shallow, so each shared [min, max] is frozen itself. */
function frozenRange(min: number, max: number): readonly [number, number] {
	return Object.freeze([min, max] as const);
}

/** Object.freeze is shallow, so each scale's own record is frozen too. */
function scaleDefaults(
	yDomain: readonly [number, number] | null,
	yTickInterval: number | null,
	mean: number | null,
	sd: number | null,
): ChartScoreScaleDefaults {
	return Object.freeze({ yDomain, yTickInterval, mean, sd });
}

/**
 * Axis and distribution conventions per score scale. Percentile ranks are
 * not normal-linear, so they carry a center but no sd; raw has no frame.
 * The canonical source for every mean/sd lookup in the chart code. The
 * tuples are shared (GRAPH_SCALE_DEFAULTS reuses them) and frozen: copy
 * them before writing one into a chart config.
 */
export const CHART_SCORE_SCALE_DEFAULTS: Readonly<
	Record<ChartScoreScale, ChartScoreScaleDefaults>
> = Object.freeze({
	percentile: scaleDefaults(frozenRange(0, 100), 25, 50, null),
	standardScore: scaleDefaults(frozenRange(40, 160), 15, 100, 15),
	tScore: scaleDefaults(frozenRange(30, 90), 10, 50, 10),
	scaledScore: scaleDefaults(frozenRange(1, 19), 3, 10, 3),
	zScore: scaleDefaults(frozenRange(-3, 3), 1, 0, 1),
	stanine: scaleDefaults(frozenRange(1, 9), 1, 5, 2),
	raw: scaleDefaults(null, null, null, null),
});

/**
 * Values a score scale can report, as opposed to the range it is displayed
 * on (CHART_SCORE_SCALE_DEFAULTS.yDomain). Clinical T scores run past the
 * 30–90 display range (BASC-3 and CBCL cap at 100), so a label naming two
 * units is decided against these ranges, not the display defaults.
 */
export const CHART_SCORE_SCALE_VALID_RANGE: Readonly<
	Record<ChartScoreScale, readonly [number, number] | null>
> = Object.freeze({
	percentile: frozenRange(0, 100),
	standardScore: frozenRange(0, 200),
	tScore: frozenRange(0, 120),
	scaledScore: frozenRange(1, 19),
	zScore: frozenRange(-5, 5),
	stanine: frozenRange(1, 9),
	raw: null,
});

/**
 * Most ticks a derived y axis may carry. Past this the tick list is not
 * readable, and building it from a tiny interval on a wide domain would
 * stall the renderer, so deriveYAxis leaves the ticks to recharts instead.
 */
export const MAX_AXIS_TICKS = 200;

/**
 * Largest magnitude a plotted value may have. Values past this (1e12
 * outliers, "1e20" strings) are not chart data: they are dropped from the
 * axis extent so they cannot widen a domain into millions of ticks.
 */
export const MAX_CHART_AXIS_VALUE = 1e9;

/** Bounds of chartConfigSchema.yTickInterval; the editor accepts the same. */
export const MIN_CHART_Y_TICK_INTERVAL = 0.01;
export const MAX_CHART_Y_TICK_INTERVAL = 1e6;

/** GraphPreset.scale → the score scale its chart config plots. */
export const GRAPH_SCALE_TO_CHART_SCORE_SCALE: Record<
	GraphScaleValue,
	ChartScoreScale
> = {
	T_SCORE: "tScore",
	STANDARD_SCORE: "standardScore",
	SCALED_SCORE: "scaledScore",
	PERCENTILE: "percentile",
	RAW: "raw",
};

interface GraphScaleDefaults {
	readonly yDomain: readonly [number, number] | null;
	readonly tickInterval: number | null;
	readonly meanLineY: number | null;
	readonly normBand: Readonly<{ y1: number; y2: number }> | null;
}

function graphScaleDefaults(scale: GraphScaleValue): GraphScaleDefaults {
	const { yDomain, yTickInterval, mean, sd } =
		CHART_SCORE_SCALE_DEFAULTS[GRAPH_SCALE_TO_CHART_SCORE_SCALE[scale]];
	// Percentile ranks have a center but no sd, so they get no mean line and
	// no average band; RAW has no frame at all.
	const normal = mean !== null && sd !== null;
	return Object.freeze({
		yDomain,
		tickInterval: yTickInterval,
		meanLineY: normal ? mean : null,
		normBand: normal ? Object.freeze({ y1: mean - sd, y2: mean + sd }) : null,
	});
}

/**
 * Scale-aware prefills for the define-graph editor and extraction prompts,
 * derived from CHART_SCORE_SCALE_DEFAULTS: T-scores have mean 50 and a
 * 40-60 average band; standard scores use M=100/SD=15; scaled scores use
 * M=10/SD=3; percentiles span 0-100. RAW has no universal frame.
 * Frozen at every level: copy a normBand before writing it into a config.
 */
export const GRAPH_SCALE_DEFAULTS: Readonly<
	Record<GraphScaleValue, GraphScaleDefaults>
> = Object.freeze({
	T_SCORE: graphScaleDefaults("T_SCORE"),
	STANDARD_SCORE: graphScaleDefaults("STANDARD_SCORE"),
	SCALED_SCORE: graphScaleDefaults("SCALED_SCORE"),
	PERCENTILE: graphScaleDefaults("PERCENTILE"),
	RAW: graphScaleDefaults("RAW"),
});

/** Score scale → the GraphPreset.scale a saved frame gets; unmapped scales are RAW. */
export const CHART_SCORE_SCALE_TO_GRAPH_SCALE: Record<
	ChartScoreScale,
	GraphScaleValue
> = {
	percentile: "PERCENTILE",
	tScore: "T_SCORE",
	standardScore: "STANDARD_SCORE",
	scaledScore: "SCALED_SCORE",
	zScore: "RAW",
	stanine: "RAW",
	raw: "RAW",
};

/**
 * What a higher score means on the preset's scale. Report prose reads a
 * BASC T of 70 as difficulty and a WISC index of 130 as strength; the
 * direction tells the writer which way the frame points.
 */
export const GRAPH_SCORE_DIRECTIONS = [
	"higherIsBetter",
	"higherIsWorse",
	"neutral",
] as const;
export type GraphScoreDirection = (typeof GRAPH_SCORE_DIRECTIONS)[number];

/** One catalog text in both UI locales; the API resolves it for the caller. */
export interface BuiltInGraphPresetText {
	fr: string;
	en: string;
}

/** A catalog series: a chartConfigSchema `yKeys` entry with a bilingual label. */
export interface BuiltInGraphPresetSeries {
	key: string;
	label: BuiltInGraphPresetText;
	marker?: string;
	lineStyle?: string;
}

/** A sample row: the `xKey` column is bilingual, every series column is a number. */
export type BuiltInGraphPresetRow = Record<
	string,
	BuiltInGraphPresetText | number
>;

/**
 * chartConfigSchema-shaped frame without its title. Every display string is
 * bilingual; the API resolves them for one locale, then the frame parses
 * against chartConfigSchema. Keys, ids and numbers never change with locale.
 */
export interface BuiltInGraphPresetConfig {
	chartType: string;
	xKey: string;
	yKeys: BuiltInGraphPresetSeries[];
	data: BuiltInGraphPresetRow[];
	yDomain?: [number, number];
	yTickInterval?: number;
	yLabel?: BuiltInGraphPresetText;
	xTickAngle?: number;
	lineInterpolation?: string;
	mirrorYAxis?: boolean;
	showLegend?: boolean;
	referenceLines?: {
		y: number;
		label: BuiltInGraphPresetText;
		strokeDasharray?: string;
	}[];
	referenceBands?: {
		y1: number;
		y2: number;
		label: BuiltInGraphPresetText;
		labelStyle?: string;
		color?: string;
	}[];
	normalCurve?: {
		mean: number;
		sd: number;
		sdMarkerSuffix: BuiltInGraphPresetText;
		zones: { fromSd: number; toSd: number; label: BuiltInGraphPresetText }[];
		tracks: { label: BuiltInGraphPresetText; type: string }[];
	};
}

export interface BuiltInGraphPreset {
	id: string;
	name: BuiltInGraphPresetText;
	description: BuiltInGraphPresetText;
	/** Chart title; the API writes the resolved text into `config.title`. */
	title: BuiltInGraphPresetText;
	testKey?: string;
	scale: GraphScaleValue;
	direction: GraphScoreDirection;
	/** The score document a report carries for this frame. */
	expectedDocumentName?: BuiltInGraphPresetText;
	config: BuiltInGraphPresetConfig;
}

const MEAN_LABEL: BuiltInGraphPresetText = { fr: "Moyenne", en: "Mean" };
const AVERAGE_ZONE_LABEL: BuiltInGraphPresetText = {
	fr: "Zone moyenne",
	en: "Average range",
};
const STANDARD_SCORE_LABEL: BuiltInGraphPresetText = {
	fr: "Score standard",
	en: "Standard score",
};
const T_SCORE_LABEL: BuiltInGraphPresetText = { fr: "Score T", en: "T score" };
const PERCENTILE_RANK_LABEL: BuiltInGraphPresetText = {
	fr: "Rang centile",
	en: "Percentile rank",
};

/**
 * Whether the Klarify graph catalog reaches users. Off: the Resources
 * library and Studio show no Klarify presets, and Klara's section chat
 * and imported figure slots suggest none. Existing forks stay untouched.
 */
export const BUILT_IN_GRAPH_PRESETS_VISIBLE = false;

/**
 * Klarify-seeded preset catalog, modelled on the report-template corpus
 * (docs/plans/report-template-gap-analysis.md, R7 P1). A clinic forks an
 * entry into its own library (`sourceBuiltInId`) before a template binds
 * it; the catalog itself is never referenced by id from a template.
 */
export const BUILT_IN_GRAPH_PRESETS: readonly BuiltInGraphPreset[] = [
	{
		id: "basc3-t-profile",
		name: {
			fr: "Profil des scores T — BASC-3",
			en: "T-score profile — BASC-3",
		},
		description: {
			fr: "Profil en ligne des échelles cliniques et adaptatives d'un questionnaire BASC-3, avec les zones moyenne, à risque et cliniquement significative.",
			en: "Line profile of the clinical and adaptive scales of a BASC-3 questionnaire, with the average, at-risk and clinically significant zones.",
		},
		testKey: "BASC-3",
		scale: "T_SCORE",
		direction: "higherIsWorse",
		expectedDocumentName: {
			fr: "BASC-3 — résultats",
			en: "BASC-3 — results",
		},
		title: {
			fr: "Profil des scores T — BASC-3",
			en: "T-score profile — BASC-3",
		},
		config: {
			chartType: "line",
			xKey: "categorie",
			yKeys: [{ key: "serie1", label: T_SCORE_LABEL, marker: "circle" }],
			data: [
				{ categorie: { fr: "Hyperactivité", en: "Hyperactivity" }, serie1: 50 },
				{ categorie: { fr: "Agressivité", en: "Aggression" }, serie1: 50 },
				{
					categorie: { fr: "Problèmes de conduite", en: "Conduct problems" },
					serie1: 50,
				},
				{ categorie: { fr: "Anxiété", en: "Anxiety" }, serie1: 50 },
				{ categorie: { fr: "Dépression", en: "Depression" }, serie1: 50 },
				{ categorie: { fr: "Somatisation", en: "Somatization" }, serie1: 50 },
				{
					categorie: { fr: "Problèmes d'attention", en: "Attention problems" },
					serie1: 50,
				},
				{
					categorie: {
						fr: "Problèmes d'apprentissage",
						en: "Learning problems",
					},
					serie1: 50,
				},
				{ categorie: { fr: "Atypicité", en: "Atypicality" }, serie1: 50 },
				{ categorie: { fr: "Retrait", en: "Withdrawal" }, serie1: 50 },
			],
			yDomain: [30, 90],
			yTickInterval: 10,
			yLabel: T_SCORE_LABEL,
			xTickAngle: -90,
			lineInterpolation: "linear",
			mirrorYAxis: true,
			referenceLines: [{ y: 50, label: MEAN_LABEL, strokeDasharray: "4 3" }],
			referenceBands: [
				{
					y1: 40,
					y2: 60,
					label: AVERAGE_ZONE_LABEL,
					color: BUILT_IN_GRAPH_PRESET_ZONE_COLORS.average,
				},
				{
					y1: 60,
					y2: 70,
					label: { fr: "À risque", en: "At risk" },
					color: BUILT_IN_GRAPH_PRESET_ZONE_COLORS.atRisk,
				},
				{
					y1: 70,
					y2: 90,
					label: {
						fr: "Cliniquement significatif",
						en: "Clinically significant",
					},
					labelStyle: "watermark",
					color: BUILT_IN_GRAPH_PRESET_ZONE_COLORS.clinical,
				},
			],
		},
	},
	{
		id: "wisc-index-profile",
		name: {
			fr: "Profil des indices — WISC-V / WAIS-IV",
			en: "Index profile — WISC-V / WAIS-IV",
		},
		description: {
			fr: "Profil en ligne des indices d'une échelle de Wechsler (scores standard, moyenne 100, écart-type 15) avec la zone moyenne 85–115.",
			en: "Line profile of the indexes of a Wechsler scale (standard scores, mean 100, standard deviation 15) with the 85–115 average zone.",
		},
		testKey: "WISC-V",
		scale: "STANDARD_SCORE",
		direction: "higherIsBetter",
		expectedDocumentName: {
			fr: "WISC-V — sommaire des indices",
			en: "WISC-V — index summary",
		},
		title: { fr: "Profil des indices — WISC-V", en: "Index profile — WISC-V" },
		config: {
			chartType: "line",
			xKey: "categorie",
			yKeys: [{ key: "serie1", label: STANDARD_SCORE_LABEL, marker: "circle" }],
			data: [
				{
					categorie: {
						fr: "Compréhension verbale (ICV)",
						en: "Verbal Comprehension (VCI)",
					},
					serie1: 100,
				},
				{
					categorie: { fr: "Visuospatial (IVS)", en: "Visual Spatial (VSI)" },
					serie1: 100,
				},
				{
					categorie: {
						fr: "Raisonnement fluide (IRF)",
						en: "Fluid Reasoning (FRI)",
					},
					serie1: 100,
				},
				{
					categorie: {
						fr: "Mémoire de travail (IMT)",
						en: "Working Memory (WMI)",
					},
					serie1: 100,
				},
				{
					categorie: {
						fr: "Vitesse de traitement (IVT)",
						en: "Processing Speed (PSI)",
					},
					serie1: 100,
				},
				{
					categorie: { fr: "Échelle globale (EGQI)", en: "Full Scale (FSIQ)" },
					serie1: 100,
				},
			],
			yDomain: [40, 160],
			yTickInterval: 15,
			yLabel: STANDARD_SCORE_LABEL,
			xTickAngle: -45,
			lineInterpolation: "linear",
			mirrorYAxis: true,
			referenceLines: [{ y: 100, label: MEAN_LABEL, strokeDasharray: "4 3" }],
			referenceBands: [
				{
					y1: 85,
					y2: 115,
					label: AVERAGE_ZONE_LABEL,
					color: BUILT_IN_GRAPH_PRESET_ZONE_COLORS.average,
				},
			],
		},
	},
	{
		id: "attention-executive-percentile-profile",
		name: {
			fr: "Profil attentionnel et exécutif — rangs centiles",
			en: "Attention and executive profile — percentile ranks",
		},
		description: {
			fr: "Histogramme des rangs centiles aux mesures d'attention et de fonctions exécutives, avec les seuils du 16e et du 84e centile.",
			en: "Bar chart of percentile ranks on attention and executive-function measures, with the 16th and 84th percentile cutoffs.",
		},
		scale: "PERCENTILE",
		direction: "higherIsBetter",
		expectedDocumentName: {
			fr: "Attention et fonctions exécutives — résultats",
			en: "Attention and executive functions — results",
		},
		title: {
			fr: "Profil attentionnel et exécutif",
			en: "Attention and executive profile",
		},
		config: {
			chartType: "bar",
			xKey: "categorie",
			yKeys: [{ key: "serie1", label: PERCENTILE_RANK_LABEL }],
			data: [
				{
					categorie: { fr: "Attention soutenue", en: "Sustained attention" },
					serie1: 50,
				},
				{
					categorie: { fr: "Attention sélective", en: "Selective attention" },
					serie1: 50,
				},
				{
					categorie: { fr: "Attention divisée", en: "Divided attention" },
					serie1: 50,
				},
				{ categorie: { fr: "Inhibition", en: "Inhibition" }, serie1: 50 },
				{ categorie: { fr: "Flexibilité", en: "Flexibility" }, serie1: 50 },
				{
					categorie: { fr: "Mémoire de travail", en: "Working memory" },
					serie1: 50,
				},
				{ categorie: { fr: "Planification", en: "Planning" }, serie1: 50 },
			],
			yDomain: [0, 100],
			yTickInterval: 25,
			yLabel: PERCENTILE_RANK_LABEL,
			xTickAngle: -45,
			referenceLines: [
				{
					y: 16,
					label: { fr: "16e centile", en: "16th percentile" },
					strokeDasharray: "4 3",
				},
				{
					y: 84,
					label: { fr: "84e centile", en: "84th percentile" },
					strokeDasharray: "4 3",
				},
			],
		},
	},
	{
		id: "vineland3-domain-profile",
		name: {
			fr: "Profil des domaines — Vineland-3",
			en: "Domain profile — Vineland-3",
		},
		description: {
			fr: "Profil en ligne des domaines adaptatifs du Vineland-3 (scores standard) avec la zone moyenne 85–115.",
			en: "Line profile of the Vineland-3 adaptive domains (standard scores) with the 85–115 average zone.",
		},
		testKey: "Vineland-3",
		scale: "STANDARD_SCORE",
		direction: "higherIsBetter",
		expectedDocumentName: {
			fr: "Vineland-3 — résultats",
			en: "Vineland-3 — results",
		},
		title: {
			fr: "Profil des domaines — Vineland-3",
			en: "Domain profile — Vineland-3",
		},
		config: {
			chartType: "line",
			xKey: "categorie",
			yKeys: [
				{
					key: "serie1",
					label: { fr: "Parent", en: "Parent" },
					marker: "circle",
					lineStyle: "solid",
				},
				{
					key: "serie2",
					label: { fr: "Enseignant", en: "Teacher" },
					marker: "diamond",
					lineStyle: "dashed",
				},
			],
			data: [
				{
					categorie: { fr: "Communication", en: "Communication" },
					serie1: 100,
					serie2: 100,
				},
				{
					categorie: { fr: "Vie quotidienne", en: "Daily living skills" },
					serie1: 100,
					serie2: 100,
				},
				{
					categorie: { fr: "Socialisation", en: "Socialization" },
					serie1: 100,
					serie2: 100,
				},
				{
					categorie: { fr: "Motricité", en: "Motor skills" },
					serie1: 100,
					serie2: 100,
				},
				{
					categorie: {
						fr: "Note composite",
						en: "Adaptive behavior composite",
					},
					serie1: 100,
					serie2: 100,
				},
			],
			yDomain: [40, 160],
			yTickInterval: 15,
			yLabel: STANDARD_SCORE_LABEL,
			lineInterpolation: "linear",
			mirrorYAxis: true,
			showLegend: true,
			referenceLines: [{ y: 100, label: MEAN_LABEL, strokeDasharray: "4 3" }],
			referenceBands: [
				{
					y1: 85,
					y2: 115,
					label: AVERAGE_ZONE_LABEL,
					color: BUILT_IN_GRAPH_PRESET_ZONE_COLORS.average,
				},
			],
		},
	},
	{
		id: "french-descriptor-normal-curve",
		name: {
			fr: "Courbe normale — descripteurs qualitatifs",
			en: "Normal curve — qualitative descriptors",
		},
		description: {
			fr: "Courbe normale (moyenne 100, écart-type 15) avec les descripteurs qualitatifs et les pistes score standard, score T, rang centile et score pondéré.",
			en: "Normal curve (mean 100, standard deviation 15) with the qualitative descriptors and the standard score, T-score, percentile rank and scaled score tracks.",
		},
		scale: "STANDARD_SCORE",
		direction: "higherIsBetter",
		title: { fr: "Sommaire des scores", en: "Score summary" },
		config: {
			chartType: "normalCurve",
			xKey: "",
			yKeys: [],
			data: [],
			normalCurve: {
				mean: 100,
				sd: 15,
				sdMarkerSuffix: { fr: "ÉT", en: "SD" },
				zones: [
					{
						fromSd: -4,
						toSd: -2,
						label: { fr: "Très faible", en: "Very low" },
					},
					{ fromSd: -2, toSd: -1.33, label: { fr: "Faible", en: "Low" } },
					{
						fromSd: -1.33,
						toSd: -0.67,
						label: { fr: "Moyen faible", en: "Low average" },
					},
					{ fromSd: -0.67, toSd: 0.67, label: { fr: "Moyen", en: "Average" } },
					{
						fromSd: 0.67,
						toSd: 1.33,
						label: { fr: "Moyen élevé", en: "High average" },
					},
					{ fromSd: 1.33, toSd: 2, label: { fr: "Élevé", en: "High" } },
					{ fromSd: 2, toSd: 4, label: { fr: "Très élevé", en: "Very high" } },
				],
				tracks: [
					{ label: STANDARD_SCORE_LABEL, type: "standardScore" },
					{ label: T_SCORE_LABEL, type: "tScore" },
					{
						label: { fr: "Score pondéré", en: "Scaled score" },
						type: "scaledScore",
					},
					{ label: PERCENTILE_RANK_LABEL, type: "percentile" },
				],
			},
		},
	},
];

/** Width a graph takes in the exported page, as a share of the content width. */
export const CHART_EXPORT_WIDTHS = ["full", "three-quarter", "half"] as const;
export type ChartExportWidth = (typeof CHART_EXPORT_WIDTHS)[number];
export const CHART_EXPORT_WIDTH_FRACTIONS: Record<ChartExportWidth, number> = {
	full: 1,
	"three-quarter": 0.75,
	half: 0.5,
};
export const DEFAULT_CHART_EXPORT_WIDTH: ChartExportWidth = "full";
/** `<img data-export-width="0.75">`: the share of the content width the exporters give a rasterized chart. */
export const CHART_EXPORT_WIDTH_ATTR = "data-export-width";
/**
 * SVG width of a full-width chart. Narrower widths render at a proportional
 * SVG width so labels keep their size relative to the chart on the page.
 */
export const CHART_EXPORT_BASE_WIDTH_PX = 800;
/** Marks a chart's data table in the live DOM so a client capture can leave it out. */
export const CHART_DATA_TABLE_ATTR = "data-chart-data-table";
/** On the chart node wrapper: "false" when the export leaves the data table out. */
export const CHART_EXPORT_TABLE_ATTR = "data-export-table";
